import { _ as _objectSpread2, t as transition, a as _defineProperty, r as resetFocus, d as defineColors } from './transitions-0e124def.esm.js';
import { cssVar as cssVar$1 } from '@chakra-ui/theme-tools';
import { d as disabledSelector, h as hoverSelector, a as activeSelector } from './button-1b672cc0.esm.js';
import '@hover/blueprint-theme';
import '@hover/utilities';
import 'ramda/src/values';
import 'ramda/src/mapObjIndexed';
import 'ramda/src/map';
import 'ramda/src/mergeDeepWith';
import '@hover/tokens-typography';
import { iconSizes, colors as colors$1 } from '@hover/blueprint-theme/next';

var cssVar = function cssVar(name) {
  return cssVar$1("button-".concat(name));
};
var $background = /*#__PURE__*/cssVar('background');
var $backgroundHover = /*#__PURE__*/cssVar('background-hover');
var $backgroundActive = /*#__PURE__*/cssVar('background-active');
var $color = /*#__PURE__*/cssVar('color');
var $border = /*#__PURE__*/cssVar('border');
var $borderDisabled = /*#__PURE__*/cssVar('border-disabled');
var $focus = /*#__PURE__*/cssVar('focus');
var $focusWidth = /*#__PURE__*/cssVar('focus-width');
var sizes = {
  small: {
    textStyle: 'body400',
    'div > span, button > span': {
      textStyle: 'body400WithCapsize'
    },
    height: '300',
    px: '300'
  },
  medium: {
    textStyle: 'body500',
    'div > span, button > span': {
      textStyle: 'body500WithCapsize'
    },
    height: '400',
    px: '500'
  },
  large: {
    textStyle: 'body500',
    'div > span, button > span': {
      textStyle: 'body500WithCapsize'
    },
    height: '500',
    px: '600'
  }
};
var Button = {
  // NOTE: See below
  baseStyle: function baseStyle() {
    var _objectSpread2$1;
    return _objectSpread2(_objectSpread2((_objectSpread2$1 = {
      alignItems: 'center',
      borderRadius: 'round',
      borderStyle: 'solid',
      background: $background.reference,
      color: $color.reference,
      borderWidth: '400'
    }, _defineProperty(_objectSpread2$1, $border.variable, 'transparent'), _defineProperty(_objectSpread2$1, $focusWidth.variable, '3px'), _defineProperty(_objectSpread2$1, "borderColor", $border.reference), _defineProperty(_objectSpread2$1, "boxSizing", 'border-box'), _defineProperty(_objectSpread2$1, "display", 'flex'), _defineProperty(_objectSpread2$1, "fontFamily", 'body'), _defineProperty(_objectSpread2$1, "fontWeight", 'bodySemiBold'), _defineProperty(_objectSpread2$1, "position", 'relative'), _defineProperty(_objectSpread2$1, "justifyContent", 'center'), _defineProperty(_objectSpread2$1, "cursor", 'pointer'), _defineProperty(_objectSpread2$1, "clipPath", 'border-box'), _defineProperty(_objectSpread2$1, "textDecoration", 'none'), _defineProperty(_objectSpread2$1, "_after", _objectSpread2({
      position: 'absolute',
      content: '""',
      top: -1,
      left: -1,
      right: -1,
      bottom: -1,
      shadow: 'inset 0 0 0 0px transparent',
      borderRadius: 'inherit',
      zIndex: 1
    }, transition.easing250('box-shadow'))), _defineProperty(_objectSpread2$1, '&[data-loading]', {
      cursor: 'default'
    }), _defineProperty(_objectSpread2$1, disabledSelector, {
      cursor: 'default',
      color: 'neutral.600',
      _dark: {
        color: 'neutral.700'
      }
    }), _defineProperty(_objectSpread2$1, hoverSelector, {
      backgroundColor: $backgroundHover.reference
    }), _defineProperty(_objectSpread2$1, activeSelector, {
      backgroundColor: $backgroundActive.reference
    }), _defineProperty(_objectSpread2$1, '&[data-checkbox], &[data-radio]', {
      _focusVisible: {
        _after: {
          shadow: "inset 0 0 0 ".concat($focusWidth.reference, " ").concat($focus.reference)
        }
      }
    }), _objectSpread2$1), resetFocus({
      _after: {
        shadow: "inset 0 0 0 ".concat($focusWidth.reference, " ").concat($focus.reference)
      }
    }, {
      _after: {
        shadow: 'inset 0 0 0 0px transparent'
      }
    })), transition.easing250('color', 'background', 'border', 'box-shadow'));
  },
  // NOTE: For some reason, we have to use functions instead of just objects when
  // using `cssVar('name').variable` to set these variables, otherwise they don't
  // end up getting set in the CSS when deployed in the production build...
  //
  // (same thing I ran into in the slider theme, thank goodness
  // I remembered the issue there and saved myself some time 😅)
  variants: {
    primary: function primary() {
      var _dark, _ref;
      return _ref = {}, _defineProperty(_ref, $background.variable, 'colors.neutral.900'), _defineProperty(_ref, $backgroundHover.variable, 'colors.neutral.800'), _defineProperty(_ref, $backgroundActive.variable, 'colors.neutral.700'), _defineProperty(_ref, $color.variable, 'colors.neutral.0'), _defineProperty(_ref, $focus.variable, 'colors.lightAlpha.600'), _defineProperty(_ref, disabledSelector, _defineProperty({}, $background.variable, 'colors.neutral.400')), _defineProperty(_ref, "_dark", (_dark = {}, _defineProperty(_dark, $background.variable, 'colors.neutral.0'), _defineProperty(_dark, $backgroundHover.variable, 'colors.neutral.100'), _defineProperty(_dark, $backgroundActive.variable, 'colors.neutral.200'), _defineProperty(_dark, $color.variable, 'colors.neutral.900'), _defineProperty(_dark, $focus.variable, 'colors.darkAlpha.500'), _defineProperty(_dark, disabledSelector, _defineProperty({}, $background.variable, 'colors.neutral.500')), _dark)), _ref;
    },
    secondary: function secondary() {
      var _dark2, _ref2;
      return _ref2 = {}, _defineProperty(_ref2, $background.variable, 'colors.neutral.200'), _defineProperty(_ref2, $backgroundHover.variable, 'colors.neutral.300'), _defineProperty(_ref2, $backgroundActive.variable, 'colors.neutral.400'), _defineProperty(_ref2, $color.variable, 'colors.neutral.900'), _defineProperty(_ref2, $focus.variable, 'colors.darkAlpha.300'), _defineProperty(_ref2, disabledSelector, _defineProperty({}, $background.variable, 'colors.neutral.400')), _defineProperty(_ref2, "_dark", (_dark2 = {}, _defineProperty(_dark2, $background.variable, 'colors.neutral.700'), _defineProperty(_dark2, $backgroundHover.variable, 'colors.neutral.600'), _defineProperty(_dark2, $backgroundActive.variable, 'colors.neutral.500'), _defineProperty(_dark2, $color.variable, 'colors.neutral.0'), _defineProperty(_dark2, $focus.variable, 'colors.lightAlpha.400'), _defineProperty(_dark2, disabledSelector, _defineProperty({}, $background.variable, 'colors.neutral.500')), _dark2)), _ref2;
    },
    tertiary: function tertiary() {
      var _disabledSelector5, _disabledSelector6, _dark3, _dataCheckbox, _disabledSelector7, _checked, _dataCheckbox2, _ref3;
      return _ref3 = {}, _defineProperty(_ref3, $background.variable, 'transparent'), _defineProperty(_ref3, $backgroundHover.variable, 'transparent'), _defineProperty(_ref3, $backgroundActive.variable, 'colors.darkAlpha.100'), _defineProperty(_ref3, $color.variable, 'colors.neutral.900'), _defineProperty(_ref3, $focus.variable, 'colors.darkAlpha.400'), _defineProperty(_ref3, $border.variable, 'currentColor'), _defineProperty(_ref3, $focusWidth.variable, '4px'), _defineProperty(_ref3, ':hover:not([disabled]):not([data-disabled])', _defineProperty({
        shadow: "inset 0 0 0 1px ".concat($border.reference)
      }, $focusWidth.variable, '5px')), _defineProperty(_ref3, disabledSelector, (_disabledSelector5 = {}, _defineProperty(_disabledSelector5, $border.variable, 'transparent'), _defineProperty(_disabledSelector5, $borderDisabled.variable, 'colors.darkAlpha.300'), _defineProperty(_disabledSelector5, $color.variable, 'colors.darkAlpha.500'), _defineProperty(_disabledSelector5, "_after", {
        shadow: "inset 0 0 0 2px ".concat($borderDisabled.reference)
      }), _disabledSelector5)), _defineProperty(_ref3, "_dark", (_dark3 = {}, _defineProperty(_dark3, $backgroundActive.variable, 'colors.lightAlpha.100'), _defineProperty(_dark3, $color.variable, 'colors.neutral.0'), _defineProperty(_dark3, $focus.variable, 'colors.lightAlpha.500'), _defineProperty(_dark3, disabledSelector, (_disabledSelector6 = {}, _defineProperty(_disabledSelector6, $borderDisabled.variable, 'colors.lightAlpha.300'), _defineProperty(_disabledSelector6, "color", 'lightAlpha.500'), _disabledSelector6)), _dark3)), _defineProperty(_ref3, '&[data-checkbox]', (_dataCheckbox = {}, _defineProperty(_dataCheckbox, $backgroundHover.variable, 'colors.darkAlpha.50'), _defineProperty(_dataCheckbox, "_dark", _defineProperty({}, $backgroundHover.variable, 'colors.lightAlpha.50')), _dataCheckbox)), _defineProperty(_ref3, '&[data-checkbox], &[data-radio]', (_dataCheckbox2 = {}, _defineProperty(_dataCheckbox2, $border.variable, 'colors.darkAlpha.400'), _defineProperty(_dataCheckbox2, "_dark", _defineProperty({}, $border.variable, 'colors.lightAlpha.400')), _defineProperty(_dataCheckbox2, disabledSelector, (_disabledSelector7 = {}, _defineProperty(_disabledSelector7, $border.variable, 'colors.darkAlpha.300'), _defineProperty(_disabledSelector7, "_dark", _defineProperty({}, $border.variable, 'colors.lightAlpha.300')), _defineProperty(_disabledSelector7, "_after", {
        shadow: 'none'
      }), _disabledSelector7)), _defineProperty(_dataCheckbox2, "_checked", (_checked = {}, _defineProperty(_checked, $border.variable, 'colors.neutral.900'), _defineProperty(_checked, "_dark", _defineProperty({}, $border.variable, 'colors.neutral.0')), _defineProperty(_checked, "shadow", "inset 0 0 0 1px ".concat($border.reference)), _defineProperty(_checked, $focusWidth.variable, '5px'), _defineProperty(_checked, ':hover:not([disabled]):not([data-disabled])', _defineProperty({
        shadow: "inset 0 0 0 2px ".concat($border.reference)
      }, $focusWidth.variable, '6px')), _checked)), _dataCheckbox2)), _ref3;
    },
    ghost: function ghost() {
      var _dark8, _ref4;
      return _ref4 = {}, _defineProperty(_ref4, $background.variable, 'transparent'), _defineProperty(_ref4, $backgroundHover.variable, 'colors.darkAlpha.200'), _defineProperty(_ref4, $backgroundActive.variable, 'colors.darkAlpha.300'), _defineProperty(_ref4, $color.variable, 'colors.neutral.900'), _defineProperty(_ref4, $focus.variable, 'colors.darkAlpha.400'), _defineProperty(_ref4, disabledSelector, {
        color: 'darkAlpha.500'
      }), _defineProperty(_ref4, "_dark", (_dark8 = {}, _defineProperty(_dark8, $background.variable, 'transparent'), _defineProperty(_dark8, $backgroundHover.variable, 'colors.lightAlpha.200'), _defineProperty(_dark8, $backgroundActive.variable, 'colors.lightAlpha.300'), _defineProperty(_dark8, $color.variable, 'colors.neutral.0'), _defineProperty(_dark8, $focus.variable, 'colors.lightAlpha.400'), _defineProperty(_dark8, disabledSelector, {
        color: 'lightAlpha.500'
      }), _dark8)), _ref4;
    }
  },
  sizes: /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({}, sizes), {}, {
    // For backwards compatability with previous theme
    "default": sizes.medium
  }),
  defaultProps: {
    colorScheme: 'neutral',
    size: 'medium',
    variant: 'primary'
  }
};

var CloseButton = {
  defaultProps: {
    size: 'default',
    variant: 'solid'
  },
  baseStyle: /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({
    border: 'none',
    borderRadius: '300',
    cursor: 'pointer',
    outline: 'none',
    // TODO(tokens/next): move to tokens
    shadow: 'inset 0 0 0 3px transparent'
  }, /*#__PURE__*/transition.easing250('background-color', 'box-shadow')), {}, {
    svg: /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({}, /*#__PURE__*/transition.easing250('color')), {}, {
      color: 'neutral.900',
      strokeLinecap: 'none',
      strokeWidth: '2'
    }),
    _focusVisible: {
      outline: 'none',
      // TODO(tokens/next): move to tokens
      shadow: 'inset 0 0 0 3px var(--hover-colors-darkAlpha-200)'
    },
    _hover: {
      bg: 'darkAlpha.100'
    }
  }),
  sizes: {
    small: {
      width: '20px',
      height: '20px',
      svg: {
        width: '50',
        height: '50'
      }
    },
    "default": {
      width: '300',
      height: '300',
      svg: {
        width: '75',
        height: '75'
      }
    },
    large: {
      width: '300',
      height: '300',
      svg: {
        width: '75',
        height: '75'
      }
    }
  },
  variants: {
    minimal: {
      bg: 'transparent',
      _hover: {}
    },
    solid: {},
    soft: {}
  }
};

var Icon = {
  baseStyle: {
    display: 'block'
  },
  defaultProps: {
    size: 'small'
  },
  sizes: iconSizes,
  variants: {}
};

var components = {
  Button: Button,
  CloseButton: CloseButton,
  Icon: Icon,
  isNext: true
};

var colors = /*#__PURE__*/defineColors(colors$1);

export { components as a, colors as c };
